/** Utilidades para identificación de clientes (directo / empresa / externos). */

export const onlyDigits = (value: string): string =>
  (value || "").replace(/\D/g, "");

/**
 * Prefijos típicos de CUIT jurídico (empresa); si una persona carga 11 dígitos con esto,
 * suele ser confusión con empresa.
 */
export const EMPRESA_CUIT_PREFIXES = [30, 33, 34];

/** CUIT que parece jurídico (30, 33, 34) sin validar dígito verificador. */
export function looksLikeEmpresaCuitPrefix(value: string): boolean {
  const d = onlyDigits(value);
  if (d.length !== 11) return false;
  const pref = parseInt(d.slice(0, 2), 10);
  return EMPRESA_CUIT_PREFIXES.includes(pref);
}

/**
 * Nombre/apellido repetidos o uno contenido en otro (copiar/pegar típico).
 */
export function looksLikeCompanyName(nombre: string, apellido: string): boolean {
  const n = normalizeForCompare(nombre);
  const a = normalizeForCompare(apellido);
  if (!n || !a) return false;
  return n === a || n.includes(a) || a.includes(n);
}

function normalizeForCompare(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

/**
 * Una persona tiene apellido; una empresa no lo necesita: su segundo campo es
 * la sucursal, opcional. (El backend además exime a Booking y Airbnb, porque la
 * plataforma a veces informa solo el nombre; en el formulario se sigue pidiendo.)
 */
export function apellidoObligatorio(esEmpresa?: boolean): boolean {
  return !esEmpresa;
}

export interface NombreCliente {
  nombre: string;
  apellido: string;
}

/**
 * Lo escrito en los dos campos de una persona, como razón social de empresa.
 *
 * Cubre el copiar/pegar (el mismo texto dos veces queda una sola vez, también
 * si uno contiene al otro) y la razón social partida en dos ("Hoch Ascensores"
 * + "S.R.L."). La sucursal queda vacía: se agrega a propósito, no por arrastre.
 */
export function unirComoRazonSocial({ nombre, apellido }: NombreCliente): NombreCliente {
  const n = nombre.trim();
  const a = apellido.trim();
  if (!a || looksLikeCompanyName(n, a)) {
    return { nombre: n.length >= a.length ? n : a, apellido: "" };
  }
  if (!n) return { nombre: a, apellido: "" };
  return { nombre: `${n} ${a}`, apellido: "" };
}

/** Etiqueta del campo de identificación (dni en modelo). */
export function getClientIdentityLabel(
  origen?: string | null,
  esEmpresa?: boolean
): string {
  const o = (origen || "directo").toLowerCase();
  if (o === "booking") return "Código Booking";
  if (o === "airbnb") return "Código Airbnb";
  if (o === "directo" && esEmpresa) return "CUIT";
  return "DNI";
}

/** Etiqueta corta para tabla (columna identificación). */
/** Etiqueta para mensajes (lista de clientes / Nueva Reserva): empresa como «Razón · Sucursal». */
export function etiquetaClienteMostrar(cliente: {
  nombre?: string | null;
  apellido?: string | null;
  es_empresa?: boolean;
}): string {
  const esEmp = !!cliente.es_empresa;
  const n = (cliente.nombre || "").trim();
  const a = (cliente.apellido || "").trim();
  if (esEmp && n && a) {
    return `${n} · ${a}`;
  }
  return `${n} ${a}`.trim() || "este cliente";
}

export function getClientIdKindShort(
  origen?: string | null,
  esEmpresa?: boolean
): string {
  const o = (origen || "directo").toLowerCase();
  if (o === "booking") return "Código Booking";
  if (o === "airbnb") return "Código Airbnb";
  if (o === "directo" && esEmpresa) return "CUIT";
  return "DNI";
}
